// wingPdfExport.ts - Compose a Wing Analysis report PDF (header + highlighted
// plan + space list + results table) entirely client-side and download it.

import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";

/** A logo image rasterised to a PNG data URL (for jsPDF.addImage). */
export interface LoadedImage {
  dataUrl: string;
  width: number;
  height: number;
}

export interface ReportLogos {
  /** Our brand logo (CADViewer), shown top-left of the header. */
  our?: LoadedImage | null;
  /** Customer brand logo, shown top-right of the header. */
  customer?: LoadedImage | null;
}

export interface WingPdfParams {
  /** DOM id of the CAD canvas container to capture (default "floorPlan"). */
  planElementId?: string;
  propertyName?: string;
  propertyNumber?: string;
  planName?: string;
  /** Wing spaces: number + tenant for the listing page. */
  spaces: { number: string; tenant: string }[];
  /** Wing Analysis table (labels + rows, including the average row). */
  tableData: { labels: Record<string, string>; data: Record<string, any>[] };
  fileName?: string;
  /** html2canvas scale (higher = crisper plan, larger file). */
  scale?: number;
  /** Pre-loaded brand logos for the header banner. */
  logos?: ReportLogos;
}

export const NAVY: [number, number, number] = [15, 53, 87];
export const LIGHT: [number, number, number] = [232, 240, 247];

/**
 * Load an image URL (SVG or raster) and rasterise it to a PNG data URL via a
 * canvas, so it can be embedded with jsPDF.addImage. Resolves null on failure
 * (so the report still renders without the logo).
 */
export function loadImageAsPng(src: string): Promise<LoadedImage | null> {
  return new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          // SVGs without intrinsic dimensions report 0 — fall back to a box.
          const w = img.naturalWidth || img.width || 320;
          const h = img.naturalHeight || img.height || 120;
          const canvas = document.createElement("canvas");
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d");
          if (!ctx) {
            resolve(null);
            return;
          }
          ctx.drawImage(img, 0, 0, w, h);
          resolve({ dataUrl: canvas.toDataURL("image/png"), width: w, height: h });
        } catch (_e) {
          resolve(null);
        }
      };
      img.onerror = () => resolve(null);
      img.src = src;
    } catch (_e) {
      resolve(null);
    }
  });
}

/** Load both report logos (our + customer). Missing/failed logos resolve null. */
export async function loadReportLogos(opts: {
  ourLogoUrl?: string;
  customerLogoUrl?: string;
}): Promise<ReportLogos> {
  const [our, customer] = await Promise.all([
    opts.ourLogoUrl ? loadImageAsPng(opts.ourLogoUrl) : Promise.resolve(null),
    opts.customerLogoUrl
      ? loadImageAsPng(opts.customerLogoUrl)
      : Promise.resolve(null),
  ]);
  return { our, customer };
}

/**
 * Draw the logo banner at the top of a page. When a customer logo is present it
 * goes on the LEFT and our (CADViewer) logo on the RIGHT; otherwise our logo is
 * shown on the left. The CADViewer logo is rendered smaller. Returns the y just
 * below the banner (or the starting y when there are no logos).
 */
export function drawLogoBanner(
  doc: jsPDF,
  logos: ReportLogos | undefined,
  margin: number,
  startY: number
): number {
  if (!logos || (!logos.our && !logos.customer)) return startY;
  const pageW = doc.internal.pageSize.getWidth();
  const ourH = 24; // CADViewer logo — smaller
  const customerH = 34; // customer logo
  const bandH = Math.max(ourH, customerH);

  const place = (img: LoadedImage, h: number, side: "left" | "right") => {
    const w = (img.width * h) / img.height;
    const x = side === "left" ? margin : pageW - margin - w;
    doc.addImage(img.dataUrl, "PNG", x, startY, w, h);
  };

  if (logos.customer) {
    place(logos.customer, customerH, "left");
    if (logos.our) place(logos.our, ourH, "right");
  } else if (logos.our) {
    place(logos.our, ourH, "left");
  }

  // Leave room for the banner height + the following title's ascent so the
  // title doesn't overlap the logos.
  return startY + bandH + 24;
}

/** Draw a thin gray separator line between the header and the page content. */
export function drawHeaderSeparator(
  doc: jsPDF,
  margin: number,
  y: number
): number {
  const pageW = doc.internal.pageSize.getWidth();
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.8);
  doc.line(margin, y, pageW - margin, y);
  return y + 12;
}

/** Light formatting of a Wing table cell based on its column key. */
export function formatCell(key: string, value: any): string {
  if (value === undefined || value === null || value === "") return "";
  const k = key.toLowerCase();
  if (k.includes("psf")) return `$${value}`;
  if (k.includes("percent")) return `${value}%`;
  if (k.includes("gla")) {
    const n = Number(value);
    if (isNaN(n)) return String(value);
    // Group thousands with an ASCII comma. toLocaleString() (no locale) can
    // emit a non-ASCII separator (e.g. NBSP) that jsPDF's standard fonts render
    // incorrectly, so format it manually.
    const [intPart, fracPart] = String(n).split(".");
    const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return fracPart ? `${grouped}.${fracPart}` : grouped;
  }
  return String(value);
}

export function drawWingTable(
  doc: jsPDF,
  tableData: WingPdfParams["tableData"],
  margin: number,
  context?: { propertyName?: string; planName?: string; logos?: ReportLogos }
) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const availW = pageW - margin * 2;

  const keys = Object.keys(tableData.labels || {});
  if (keys.length === 0) return;

  // Weighted column widths (tenant/space get more room).
  const weightFor = (k: string) =>
    k === "tenant_name" ? 2.4 : k === "space_number" ? 1.3 : 1;
  const totalWeight = keys.reduce((s, k) => s + weightFor(k), 0);
  const colW = keys.map((k) => (availW * weightFor(k)) / totalWeight);

  const rowH = 18;
  let y = drawLogoBanner(doc, context?.logos, margin, margin);

  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("Wing Analysis — results", margin, y);
  y += 16;

  // Self-contained context line (mall name + plan) so this results page can be
  // used on its own.
  const ctxBits = [context?.propertyName, context?.planName].filter(
    Boolean
  ) as string[];
  if (ctxBits.length > 0) {
    doc.setFontSize(10.5);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(60, 60, 60);
    doc.text(ctxBits.join("  —  "), margin, y);
    y += 16;
  }

  // Gray separator between the header and the table content.
  y = drawHeaderSeparator(doc, margin, y);

  const drawHeader = () => {
    doc.setFillColor(...NAVY);
    doc.rect(margin, y, availW, rowH, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    let x = margin;
    keys.forEach((k, i) => {
      doc.text(String(tableData.labels[k] ?? k), x + 4, y + 12, {
        maxWidth: colW[i] - 6,
      });
      x += colW[i];
    });
    y += rowH;
  };

  drawHeader();

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);

  for (const row of tableData.data || []) {
    if (y + rowH > pageH - margin) {
      doc.addPage();
      y = margin;
      drawHeader();
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
    }

    const isSummary = !!row._is_summary;
    if (isSummary) {
      doc.setFillColor(...LIGHT);
      doc.rect(margin, y, availW, rowH, "F");
      doc.setFont("helvetica", "bold");
    } else {
      doc.setFont("helvetica", "normal");
    }
    doc.setTextColor(...NAVY);

    let x = margin;
    keys.forEach((k, i) => {
      doc.text(formatCell(k, row[k]), x + 4, y + 12, { maxWidth: colW[i] - 6 });
      x += colW[i];
    });

    // Row separator line
    doc.setDrawColor(220, 220, 220);
    doc.line(margin, y + rowH, margin + availW, y + rowH);
    y += rowH;
  }
}

export async function exportWingPlanPdf(params: WingPdfParams): Promise<void> {
  const {
    planElementId = "floorPlan",
    propertyName,
    propertyNumber,
    planName,
    spaces,
    tableData,
    scale = 3,
  } = params;

  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const margin = 36;

  // ---- Logo banner + header + property info ----
  let y = margin;
  y = drawLogoBanner(doc, params.logos, margin, y);
  doc.setFontSize(18);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("Wing Analysis", margin, y);
  y += 22;

  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(60, 60, 60);
  const headerLines = [
    propertyName &&
      `Property: ${propertyName}${propertyNumber ? ` (${propertyNumber})` : ""}`,
    planName && `Plan: ${planName}`,
    `Spaces: ${spaces.map((s) => s.number).join(", ")}`,
    `Generated: ${new Date().toLocaleString()}`,
  ].filter(Boolean) as string[];
  headerLines.forEach((t) => {
    doc.text(t, margin, y, { maxWidth: pageW - margin * 2 });
    y += 14;
  });
  y += 2;

  // Gray separator between the header and the content.
  y = drawHeaderSeparator(doc, margin, y);
  y += 6;

  // ---- Wing spaces list (page 1) ----
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text(`Wing Spaces (${spaces.length})`, margin, y);
  y += 18;
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(40, 40, 40);
  spaces.forEach((s) => {
    if (y > pageH - margin) {
      doc.addPage();
      y = margin;
    }
    doc.text(`${s.number}${s.tenant ? `   —   ${s.tenant}` : ""}`, margin, y);
    y += 15;
  });

  // ---- Results table page ----
  doc.addPage();
  drawWingTable(doc, tableData, margin, {
    propertyName,
    planName,
    logos: params.logos,
  });

  // ---- Highlighted plan image (LAST page) ----
  doc.addPage();
  let py = drawLogoBanner(doc, params.logos, margin, margin);
  doc.setFontSize(13);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(...NAVY);
  doc.text("Highlighted plan", margin, py);
  py += 16;
  py = drawHeaderSeparator(doc, margin, py);
  py += 6;
  const el =
    typeof document !== "undefined"
      ? document.getElementById(planElementId)
      : null;
  if (el) {
    try {
      const canvas = await html2canvas(el as HTMLElement, {
        scale,
        useCORS: true,
        backgroundColor: "#ffffff",
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");
      const availW = pageW - margin * 2;
      const availH = pageH - py - margin;
      const ratio = Math.min(availW / canvas.width, availH / canvas.height);
      const imgW = canvas.width * ratio;
      const imgH = canvas.height * ratio;
      doc.addImage(imgData, "PNG", margin, py, imgW, imgH);
    } catch (e) {
      doc.setTextColor(200, 0, 0);
      doc.text("(Could not capture the plan image.)", margin, py);
      doc.setTextColor(0, 0, 0);
    }
  } else {
    doc.setTextColor(200, 0, 0);
    doc.text("(Plan canvas not found.)", margin, py);
    doc.setTextColor(0, 0, 0);
  }

  const safeName = (propertyName || "plan").replace(/\s+/g, "_");
  const fileName = params.fileName || `Wing_Analysis_${safeName}.pdf`;
  doc.save(fileName);
}
